import { CircularProgress, Divider, Drawer, IconButton, List, styled, useTheme } from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { IAppDrawer } from "./AppDrawerInterfaces";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useStyles } from "./AppDrawer.styles";
import MaderaAspenLogo from "./images/maderaAspenLogo.png";
import MaderaLogo from "./images/maderaLogo.png";
import AppToolbar from "./components/AppToolbar/AppToolbar.view";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";

export const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

export const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
    elevation?: number;
}

const AppDrawer = ({
    isOpenAppDrawer,
    setIsOpenAppDrawer,
    appDrawerItems,
    toolbarLogo,
    appDrawerFooterItems,
    rca,
}: IAppDrawer) => {
    const { corporations, loading: loadingCorporations } = CorporationsEntity.GetMenuCorporateDivisionsRegionsMills();
    const theme = useTheme();
    const classes = useStyles();

    const handleDrawerOpen = () => {
        setIsOpenAppDrawer(true);
    };

    const handleDrawerClose = () => {
        setIsOpenAppDrawer(false);
    };

    return (
        <>
            <AppBar position="fixed" open={isOpenAppDrawer} elevation={0}>
                <AppToolbar
                    onDrawerOpen={handleDrawerOpen}
                    isOpenAppDrawer={isOpenAppDrawer}
                    toolbarLogo={toolbarLogo}
                />
            </AppBar>
            <Drawer
                className={classes.drawer}
                sx={{
                    width: drawerWidth,
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                    },
                }}
                variant="persistent"
                anchor="left"
                open={isOpenAppDrawer}
            >
                <DrawerHeader className={classes.drawerHeader}>
                    <div className={classes.drawerLogoWrapper}>
                        {loadingCorporations && !corporations && <CircularProgress size={20} />}
                        {!loadingCorporations && corporations && corporations.length > 0 && !corporations[0].media && (
                            <div className={classes.corporateEmptyImageWrapper}>
                                <span>OP</span>
                            </div>
                        )}
                        {corporations && corporations.length > 0 && corporations[0].media && (
                            <img src={corporations[0].media?.media_url} alt="logo" className={classes.drawerLogo} />
                        )}
                    </div>
                    <IconButton onClick={handleDrawerClose} className={classes.drawerHeaderIconButton}>
                        {theme.direction === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </DrawerHeader>
                <List>{appDrawerItems}</List>
                {!!appDrawerFooterItems && <Divider />}
                <div className={classes.drawerFooter}>
                    {!!appDrawerFooterItems && (
                        <div className={classes.drawerFooterWrapper}>
                            <div className={classes.drawerFooterItems}>{appDrawerFooterItems}</div>
                            <Divider className={classes.drawerDivider} />
                        </div>
                    )}
                    <img src={rca ? MaderaLogo : MaderaAspenLogo} alt="logo" className={classes.drawerFooterLogo} />
                </div>
            </Drawer>
        </>
    );
};

export default AppDrawer;
